// src/components/AiGenerateSheet.jsx
//
// Полноэкранная модалка для AI генерации изображений.
// Wizard:
//   шаг 1: выбор типа (try_on / studio_photo / on_pedestal)
//   шаг 2: загрузка фото товара (или выбор из существующих)
//   шаг 3: модель (только для try_on) или стиль подставки (для on_pedestal)
//   шаг 4: описание + кнопка «Сгенерировать»
//   шаг 5: результат + действия
//
// Props:
//   open               — bool, открыта ли модалка
//   onClose            — () => void
//   onApply            — (outputUrl: string) => void
//                        Колбэк когда юзер нажал «Применить» (главное фото)
//                        outputUrl уже полный (с https://) или относительный
//   onAddToGallery     — (outputUrl: string) => void
//                        Колбэк когда юзер нажал «Добавить в галерею»
//   existingPhotos     — string[] (опционально) — фото товара которые уже есть,
//                        пользователь может выбрать одно из них вместо загрузки
//   defaultDescription — string (опционально) — например имя товара

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ai as aiApi } from '../api';
import Icon from './Icon';

// Хост для абсолютизации относительных URL
const API_HOST = (() => {
  try {
    const u = new URL(process.env.REACT_APP_API_URL || 'https://api.alsatuu.biz/api');
    return `${u.protocol}//${u.host}`;
  } catch { return 'https://api.alsatuu.biz'; }
})();

function fullUrl(u) {
  if (!u) return '';
  if (u.startsWith('http') || u.startsWith('blob:') || u.startsWith('data:')) return u;
  return API_HOST + u;
}

export default function AiGenerateSheet({
  open, onClose,
  onApply, onAddToGallery,
  existingPhotos = [],
  defaultDescription = '',
}) {
  const { t } = useTranslation();

  // ─── state ────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);           // 'try_on'|'studio_photo'|'on_pedestal'
  const [productFile, setProductFile] = useState(null);
  const [productPreview, setProductPreview] = useState(''); // dataURL для preview
  const [productSourceUrl, setProductSourceUrl] = useState(''); // URL если выбрано из existing
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelId, setModelId] = useState(null);
  const [modelFilter, setModelFilter] = useState({ gender: '', ageGroup: '' });
  const [pedestals, setPedestals] = useState([]);
  const [pedestalsLoading, setPedestalsLoading] = useState(false);
  const [pedestalId, setPedestalId] = useState(null);
  const [pedestalFilter, setPedestalFilter] = useState({ style: '' });
  const [description, setDescription] = useState(defaultDescription);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);       // { id, outputUrl, costUsd, used, limit, ... }
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);       // { used, limit, plan, ... }
  const fileRef = useRef();

  // ─── lifecycle ────────────────────────────────────────────────
  // Сброс при открытии
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setType(null);
    setProductFile(null);
    setProductPreview('');
    setProductSourceUrl('');
    setModelId(null);
    setPedestalId(null);
    setDescription(defaultDescription);
    setResult(null);
    setError('');
    setGenerating(false);

    // Подгружаем статус лимита
    aiApi.status().then(setStatus).catch(() => setStatus(null));
  }, [open, defaultDescription]);

  // Подгружаем модели когда нужны
  useEffect(() => {
    if (!open || type !== 'try_on' || models.length > 0) return;
    setModelsLoading(true);
    aiApi.models()
      .then(r => setModels(r.items || []))
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, [open, type, models.length]);

  // Подгружаем подставки когда нужны (для on_pedestal)
  useEffect(() => {
    if (!open || type !== 'on_pedestal' || pedestals.length > 0) return;
    setPedestalsLoading(true);
    aiApi.pedestals()
      .then(r => setPedestals(r.items || []))
      .catch(() => setPedestals([]))
      .finally(() => setPedestalsLoading(false));
  }, [open, type, pedestals.length]);

  if (!open) return null;

  // ─── handlers ─────────────────────────────────────────────────
  const pickType = (newType) => {
    setType(newType);
    setStep(2);
  };

  // Когда выбран тип studio_photo, у него нет промежуточного шага 3
  // (нет модели и нет подставки). После выбора фото переходим сразу на 4.
  const advanceAfterPhoto = (chosenType) => {
    const t = chosenType || type;
    if (t === 'studio_photo') setStep(4);
    else                       setStep(3);
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      setError(t('aiGenerate.errInvalidFile'));
      return;
    }
    setProductFile(f);
    setProductSourceUrl('');
    setProductPreview(URL.createObjectURL(f));
    setError('');
    // переход на следующий шаг (зависит от типа)
    advanceAfterPhoto();
  };

  const useExisting = async (url) => {
    // Скачиваем существующее фото как Blob чтобы можно было передать в FormData
    try {
      const abs = fullUrl(url);
      const resp = await fetch(abs);
      const blob = await resp.blob();
      const file = new File([blob], 'product.jpg', { type: blob.type || 'image/jpeg' });
      setProductFile(file);
      setProductSourceUrl(url);
      setProductPreview(abs);
      setError('');
      advanceAfterPhoto();
    } catch (e) {
      setError(t('aiGenerate.errLoadExisting'));
    }
  };

  const filteredModels = models.filter(m => {
    if (modelFilter.gender   && m.gender   !== modelFilter.gender)   return false;
    if (modelFilter.ageGroup && m.ageGroup !== modelFilter.ageGroup) return false;
    return true;
  });

  const canGenerate = (() => {
    if (!productFile) return false;
    if (description.trim().length < 3) return false;
    if (type === 'try_on' && !modelId) return false;
    if (type === 'on_pedestal' && !pedestalId) return false;
    return true;
  })();

  const doGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError('');

    // Запомним время начала чтобы найти СВОЙ запрос в истории если network падёт
    const startTime = Date.now() - 5000;  // -5с на всякий случай

    try {
      const r = await aiApi.generate({
        type,
        product: productFile,
        productDescription: description.trim(),
        modelId: type === 'try_on' ? modelId : undefined,
        pedestalId: type === 'on_pedestal' ? pedestalId : undefined,
      });
      setResult(r);
      setStep(5);
      // Обновим статус (потратили генерацию)
      aiApi.status().then(setStatus).catch(() => {});
    } catch (e) {
      // Специальные коды бэка с понятным сообщением
      if (e.serverError === 'ai_limit_reached') {
        setError(t('aiGenerate.errLimitReached'));
        setGenerating(false);
        return;
      }
      if (e.serverError === 'safety_block')   { setError(t('aiGenerate.errSafetyBlock'));   setGenerating(false); return; }
      if (e.serverError === 'rate_limit')     { setError(t('aiGenerate.errRateLimit'));     setGenerating(false); return; }
      if (e.serverError === 'not_configured') { setError(t('aiGenerate.errNotConfigured')); setGenerating(false); return; }

      // Network/timeout — особый случай:
      // Бэк скорее всего продолжает работать. Проверяем не сохранилась ли уже
      // наша генерация в истории (status: success или processing).
      try {
        const h = await aiApi.history(5);
        const items = h.items || [];
        // Ищем наш запрос: type совпадает + создан после startTime
        const myItems = items.filter(g =>
          g.type === type &&
          new Date(g.createdAt).getTime() >= startTime
        );

        const finished = myItems.find(g => g.status === 'success' && g.outputUrl);
        if (finished) {
          // Запрос успел завершиться пока фронт ждал — показываем результат
          setResult({
            id: finished.id,
            outputUrl: finished.outputUrl,
            type: finished.type,
            modelId: finished.modelId,
            tokensInput: finished.tokensInput,
            tokensOutput: finished.tokensOutput,
            costUsd: finished.costUsd,
            used: (status?.used || 0) + 1,
            limit: status?.limit,
          });
          setStep(5);
          aiApi.status().then(setStatus).catch(() => {});
          setGenerating(false);
          return;
        }

        const processing = myItems.find(g => g.status === 'processing');
        if (processing) {
          // Ещё крутится — даём знать юзеру что не страшно
          setError(t('aiGenerate.errStillProcessing'));
          setGenerating(false);
          return;
        }
      } catch (_) {}

      // Если правда сетевая проблема
      if (e.netErrorKey) setError(t(e.netErrorKey));
      else setError(e.message || t('aiGenerate.errGeneric'));
      setGenerating(false);
    }
  };

  const applyAsMain = async () => {
    if (!result) return;
    try {
      // Отмечаем на бэке
      aiApi.apply(result.id, null).catch(() => {});
      onApply?.(fullUrl(result.outputUrl));
      onClose?.();
    } catch (e) {}
  };

  const addToGallery = async () => {
    if (!result) return;
    try {
      aiApi.apply(result.id, null).catch(() => {});
      onAddToGallery?.(fullUrl(result.outputUrl));
      onClose?.();
    } catch (e) {}
  };

  const regenerate = () => {
    setResult(null);
    setStep(4);
  };

  // ─── render helpers ───────────────────────────────────────────
  const limitInfo = status && status.limit != null ? `${status.used}/${status.limit}` : null;
  const limitColor = (() => {
    if (!status || status.limit == null) return 'var(--fg-muted)';
    const ratio = status.used / status.limit;
    if (ratio >= 0.9) return 'var(--danger)';
    if (ratio >= 0.7) return 'var(--warning, #f59e0b)';
    return 'var(--success, #16a34a)';
  })();

  return (
    <div style={overlayStyle}>
      <div style={sheetStyle}>
        {/* Шапка */}
        <div style={headerStyle}>
          <button onClick={onClose} style={closeBtn} aria-label="Close">
            <Icon name="x" size={20} stroke={2} />
          </button>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
            {t('aiGenerate.title')}
          </h2>
          {limitInfo && (
            <div style={{ fontSize: 12, color: limitColor, fontWeight: 600 }}>
              AI: {limitInfo}
            </div>
          )}
        </div>

        {/* Прогресс */}
        <StepIndicator current={step} total={type === 'studio_photo' ? 4 : 5} />

        {/* Контент шага */}
        <div style={contentStyle}>
          {/* ─── ШАГ 1: тип ─── */}
          {step === 1 && (
            <div>
              <p style={mutedText}>{t('aiGenerate.pickTypeHint')}</p>
              <button style={typeBtnStyle} onClick={() => pickType('try_on')}>
                <Icon name="users" size={28} />
                <div>
                  <div style={typeTitleStyle}>{t('aiGenerate.typeTryOn')}</div>
                  <div style={typeHintStyle}>{t('aiGenerate.typeTryOnHint')}</div>
                </div>
              </button>
              <button style={typeBtnStyle} onClick={() => pickType('studio_photo')}>
                <Icon name="image" size={28} />
                <div>
                  <div style={typeTitleStyle}>{t('aiGenerate.typeStudio')}</div>
                  <div style={typeHintStyle}>{t('aiGenerate.typeStudioHint')}</div>
                </div>
              </button>
              <button style={typeBtnStyle} onClick={() => pickType('on_pedestal')}>
                <Icon name="gift" size={28} />
                <div>
                  <div style={typeTitleStyle}>{t('aiGenerate.typePedestal')}</div>
                  <div style={typeHintStyle}>{t('aiGenerate.typePedestalHint')}</div>
                </div>
              </button>
            </div>
          )}

          {/* ─── ШАГ 2: фото товара ─── */}
          {step === 2 && (
            <div>
              <p style={mutedText}>{t('aiGenerate.pickPhotoHint')}</p>

              {existingPhotos.length > 0 && (
                <>
                  <div style={sectionLabel}>{t('aiGenerate.useExisting')}</div>
                  <div style={existingGrid}>
                    {existingPhotos.map((url, idx) => (
                      <button
                        key={url + idx}
                        onClick={() => useExisting(url)}
                        style={existingItem}
                      >
                        <img src={fullUrl(url)} alt="" style={imgCover} />
                      </button>
                    ))}
                  </div>
                  <div style={{ ...sectionLabel, marginTop: 16 }}>{t('aiGenerate.orUploadNew')}</div>
                </>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={pickFile}
              />
              <button onClick={() => fileRef.current?.click()} style={uploadBtn}>
                <Icon name="plus" size={24} stroke={2} />
                <span>{t('aiGenerate.uploadPhoto')}</span>
              </button>

              {error && <p style={errorText}>{error}</p>}
            </div>
          )}

          {/* ─── ШАГ 3: модель (для try_on) или подставка (для on_pedestal) ─── */}
          {step === 3 && type === 'try_on' && (
            <div>
              <p style={mutedText}>{t('aiGenerate.pickModelHint')}</p>

              {/* Фильтры */}
              <div style={chipsRow}>
                <button
                  style={chipStyle(modelFilter.gender === '')}
                  onClick={() => setModelFilter(f => ({ ...f, gender: '' }))}
                >{t('aiGenerate.filterAll')}</button>
                <button
                  style={chipStyle(modelFilter.gender === 'male')}
                  onClick={() => setModelFilter(f => ({ ...f, gender: 'male' }))}
                >{t('aiGenerate.filterMale')}</button>
                <button
                  style={chipStyle(modelFilter.gender === 'female')}
                  onClick={() => setModelFilter(f => ({ ...f, gender: 'female' }))}
                >{t('aiGenerate.filterFemale')}</button>
                <button
                  style={chipStyle(modelFilter.gender === 'kid')}
                  onClick={() => setModelFilter(f => ({ ...f, gender: 'kid' }))}
                >{t('aiGenerate.filterKid')}</button>
              </div>

              {modelsLoading && <div className="loader" style={{ padding: 24 }}><div className="spinner" /></div>}

              {!modelsLoading && filteredModels.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: 24 }}>
                  {t('aiGenerate.noModels')}
                </p>
              )}

              <div style={modelsGrid}>
                {filteredModels.map(m => (
                  <button
                    key={m.id}
                    onClick={() => { setModelId(m.id); setStep(4); }}
                    style={modelCard(modelId === m.id)}
                  >
                    <div style={modelPhotoBox}>
                      <img src={fullUrl(m.thumbnailUrl || m.photoUrl)} alt={m.name} style={imgCover} />
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>{m.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && type === 'on_pedestal' && (
            <div>
              <p style={mutedText}>{t('aiGenerate.pickPedestalHint')}</p>

              {/* Фильтры по стилю */}
              <div style={chipsRow}>
                <button
                  style={chipStyle(pedestalFilter.style === '')}
                  onClick={() => setPedestalFilter({ style: '' })}
                >{t('aiGenerate.filterAll')}</button>
                <button
                  style={chipStyle(pedestalFilter.style === 'minimalist')}
                  onClick={() => setPedestalFilter({ style: 'minimalist' })}
                >{t('aiGenerate.pedestal_minimalist')}</button>
                <button
                  style={chipStyle(pedestalFilter.style === 'wood')}
                  onClick={() => setPedestalFilter({ style: 'wood' })}
                >{t('aiGenerate.pedestal_wood')}</button>
                <button
                  style={chipStyle(pedestalFilter.style === 'marble')}
                  onClick={() => setPedestalFilter({ style: 'marble' })}
                >{t('aiGenerate.pedestal_marble')}</button>
                <button
                  style={chipStyle(pedestalFilter.style === 'glass')}
                  onClick={() => setPedestalFilter({ style: 'glass' })}
                >{t('aiGenerate.pedestal_glass')}</button>
                <button
                  style={chipStyle(pedestalFilter.style === 'metal')}
                  onClick={() => setPedestalFilter({ style: 'metal' })}
                >{t('aiGenerate.pedestal_metal')}</button>
              </div>

              {pedestalsLoading && <div className="loader" style={{ padding: 24 }}><div className="spinner" /></div>}

              {!pedestalsLoading && pedestals.filter(p => !pedestalFilter.style || p.style === pedestalFilter.style).length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--fg-muted)', padding: 24 }}>
                  {t('aiGenerate.noPedestals')}
                </p>
              )}

              <div style={modelsGrid}>
                {pedestals
                  .filter(p => !pedestalFilter.style || p.style === pedestalFilter.style)
                  .map(p => (
                    <button
                      key={p.id}
                      onClick={() => { setPedestalId(p.id); setStep(4); }}
                      style={modelCard(pedestalId === p.id)}
                    >
                      <div style={{ width: '100%', aspectRatio: '1', borderRadius: 'var(--r-sm, 8px)', overflow: 'hidden', background: 'var(--bg-subtle)' }}>
                        <img src={fullUrl(p.thumbnailUrl || p.photoUrl)} alt={p.name} style={imgCover} />
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>{p.name}</div>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* studio_photo не имеет шага 3 — он минуется при выборе фото (см. advanceAfterPhoto) */}

          {/* ─── ШАГ 4: описание + генерация ─── */}
          {step === 4 && (
            <div>
              {/* Превью входов */}
              <div style={previewRow}>
                {type === 'try_on' && (() => {
                  const m = models.find(x => x.id === modelId);
                  return m ? (
                    <div style={previewItem}>
                      <img src={fullUrl(m.photoUrl)} alt="" style={imgCover} />
                      <span style={previewLabel}>{t('aiGenerate.model')}</span>
                    </div>
                  ) : null;
                })()}
                {productPreview && (
                  <div style={previewItem}>
                    <img src={productPreview} alt="" style={imgCover} />
                    <span style={previewLabel}>{t('aiGenerate.product')}</span>
                  </div>
                )}
                {type === 'on_pedestal' && (() => {
                  const p = pedestals.find(x => x.id === pedestalId);
                  return p ? (
                    <div style={previewItem}>
                      <img src={fullUrl(p.photoUrl)} alt="" style={imgCover} />
                      <span style={previewLabel}>{t('aiGenerate.pedestal')}</span>
                    </div>
                  ) : null;
                })()}
              </div>

              {/* Поле описания */}
              <div className="input-group">
                <label>{t('aiGenerate.descriptionLabel')} *</label>
                <input
                  className="input"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  onBlur={e => setDescription(e.target.value)}
                  placeholder={t('aiGenerate.descriptionPh')}
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  autoComplete="off"
                />
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>
                  {t('aiGenerate.descriptionHint')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 4 }}>
                  {t('aiGenerate.descriptionExamples')}
                </div>
              </div>

              {error && <p style={errorText}>{error}</p>}

              <button
                onClick={() => {
                  // Снимаем фокус — это форсит клавиатуру закоммитить текущее слово
                  // (иначе автокоррекция может «съесть» последнее слово с пробелом)
                  if (document.activeElement && document.activeElement.blur) {
                    document.activeElement.blur();
                  }
                  // Небольшая задержка чтобы blur точно успел обновить state
                  setTimeout(doGenerate, 50);
                }}
                disabled={!canGenerate || generating}
                className="btn btn-primary btn-lg"
                style={{ width: '100%', marginTop: 12 }}
              >
                {generating ? (
                  <>
                    <span className="spinner" style={{ marginRight: 8 }} />
                    {t('aiGenerate.generating')}
                  </>
                ) : (
                  <><Icon name="sparkles" size={18} style={{ marginRight: 6 }} /> {t('aiGenerate.generateBtn')}</>
                )}
              </button>

              {generating && (
                <p style={{ ...mutedText, textAlign: 'center', marginTop: 12 }}>
                  {t('aiGenerate.generatingHint')}
                </p>
              )}
            </div>
          )}

          {/* ─── ШАГ 5: результат ─── */}
          {step === 5 && result && (
            <div>
              <div style={resultBox}>
                <img src={fullUrl(result.outputUrl)} alt="" style={resultImg} />
              </div>

              <div style={statsRow}>
                <span>{result.used}/{result.limit || '∞'} {t('aiGenerate.usedThisPeriod')}</span>
              </div>

              <button className="btn btn-primary btn-lg" style={fullBtn} onClick={applyAsMain}>
                <Icon name="check" size={18} style={{ marginRight: 6 }} />
                {t('aiGenerate.applyAsMain')}
              </button>
              <button className="btn btn-lg" style={fullBtn} onClick={addToGallery}>
                <Icon name="plus" size={18} style={{ marginRight: 6 }} />
                {t('aiGenerate.addToGallery')}
              </button>
              <button className="btn btn-ghost btn-lg" style={fullBtn} onClick={regenerate}>
                <Icon name="sparkles" size={18} style={{ marginRight: 6 }} />
                {t('aiGenerate.regenerate')}
              </button>
            </div>
          )}
        </div>

        {/* Кнопка «Назад» внизу */}
        {step > 1 && step < 5 && (
          <button
            onClick={() => {
              // studio_photo пропустил шаг 3, при возврате с шага 4 идём сразу на 2
              if (step === 4 && type === 'studio_photo') setStep(2);
              else setStep(step - 1);
            }}
            style={backBtn}
          >
            <Icon name="chevronLeft" size={16} /> {t('common.back')}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Step indicator ───
function StepIndicator({ current, total }) {
  return (
    <div style={{ display: 'flex', gap: 4, padding: '4px 16px 12px', background: 'var(--bg)' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i + 1 <= current ? 'var(--brand-primary)' : 'var(--border)',
            transition: 'background 0.2s',
          }}
        />
      ))}
    </div>
  );
}

// ─── styles ───
const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 100,
  background: 'var(--bg, #0f1216)',
  display: 'flex', flexDirection: 'column',
};
const sheetStyle = {
  display: 'flex', flexDirection: 'column', flex: 1,
  minHeight: 0, color: 'var(--fg, #fff)',
};
const headerStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '14px 16px 10px',
  borderBottom: '1px solid var(--border)',
};
const closeBtn = {
  background: 'transparent', border: 'none',
  padding: 4, cursor: 'pointer',
  color: 'var(--fg)', marginRight: 4,
};
const contentStyle = {
  flex: 1, overflowY: 'auto', padding: '16px',
};
const mutedText = {
  fontSize: 13, color: 'var(--fg-muted)',
  marginBottom: 16, lineHeight: 1.4,
};
const typeBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 14,
  width: '100%', padding: '14px 16px',
  background: 'var(--surface, #1a1f26)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-md, 12px)',
  color: 'var(--fg)',
  marginBottom: 10, cursor: 'pointer',
  textAlign: 'left',
};
const typeTitleStyle = { fontSize: 15, fontWeight: 600, marginBottom: 2 };
const typeHintStyle  = { fontSize: 12, color: 'var(--fg-muted)' };
const sectionLabel = {
  fontSize: 12, color: 'var(--fg-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  marginBottom: 8, fontWeight: 600,
};
const existingGrid = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
};
const existingItem = {
  aspectRatio: '1', borderRadius: 'var(--r-sm, 8px)',
  overflow: 'hidden', border: '1px solid var(--border)',
  background: 'var(--bg-subtle)', cursor: 'pointer',
  padding: 0,
};
const imgCover = { width: '100%', height: '100%', objectFit: 'cover' };
const uploadBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  width: '100%', padding: 16,
  background: 'var(--bg-subtle)',
  border: '2px dashed var(--border)',
  borderRadius: 'var(--r-md, 12px)',
  color: 'var(--fg-muted)', cursor: 'pointer',
  fontSize: 14, fontWeight: 600,
};
const errorText = {
  color: 'var(--danger)', fontSize: 13,
  marginTop: 8, padding: 10,
  background: 'var(--danger-soft, rgba(255,77,79,0.1))',
  borderRadius: 'var(--r-sm, 8px)',
};
const chipsRow = {
  display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap',
};
const chipStyle = (active) => ({
  padding: '6px 12px', fontSize: 13,
  borderRadius: 999,
  background: active ? 'var(--brand-primary)' : 'var(--surface)',
  color: active ? '#fff' : 'var(--fg)',
  border: '1px solid ' + (active ? 'var(--brand-primary)' : 'var(--border)'),
  cursor: 'pointer',
});
const modelsGrid = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
};
const modelCard = (selected) => ({
  background: 'transparent', border: 'none',
  cursor: 'pointer', padding: 4,
  textAlign: 'center',
  borderRadius: 'var(--r-sm, 8px)',
  outline: selected ? '2px solid var(--brand-primary)' : 'none',
  outlineOffset: 2,
  color: 'var(--fg)',
});
const modelPhotoBox = {
  width: '100%', aspectRatio: '3/4',
  borderRadius: 'var(--r-sm, 8px)',
  overflow: 'hidden', background: 'var(--bg-subtle)',
};
const previewRow = {
  display: 'flex', gap: 10, marginBottom: 14, justifyContent: 'center',
};
const previewItem = {
  position: 'relative',
  width: 90, aspectRatio: '3/4',
  borderRadius: 'var(--r-sm, 8px)',
  overflow: 'hidden', background: 'var(--bg-subtle)',
};
const previewLabel = {
  position: 'absolute', bottom: 0, left: 0, right: 0,
  background: 'rgba(0,0,0,0.6)',
  color: '#fff', fontSize: 10, fontWeight: 600,
  textAlign: 'center', padding: '2px 0',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};
const resultBox = {
  width: '100%', borderRadius: 'var(--r-md, 12px)',
  overflow: 'hidden', marginBottom: 12,
  background: 'var(--bg-subtle)',
};
const resultImg = {
  width: '100%', display: 'block',
};
const statsRow = {
  display: 'flex', gap: 8, justifyContent: 'center',
  marginBottom: 16, fontSize: 12, color: 'var(--fg-muted)',
};
const fullBtn = { width: '100%', marginBottom: 8 };
const backBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  background: 'transparent', border: 'none',
  padding: '12px 16px', color: 'var(--fg-muted)',
  fontSize: 14, cursor: 'pointer',
  borderTop: '1px solid var(--border)',
};
